// ResetPassword.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Fade,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';

const theme = createTheme({
  palette: {
    mode: 'light',
    background: { default: '#ffffff', paper: '#ffffff' },
    primary: { main: '#C4B08C' },
    text: { primary: '#111', secondary: '#555' },
    error: { main: '#d32f2f' },
    success: { main: '#2e7d32' },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});

const API_URL = process.env.REACT_APP_API_URL;

const ResetPassword = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token manquant');
        setVerifying(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/access/verify-reset-token/${token}`);
        const data = await res.json();

        if (res.ok && data.valid) {
          setTokenValid(true);
          setUserEmail(data.email);
        } else {
          setError(data.message || 'Token invalide ou expiré');
        }
      } catch (err) {
        setError('Erreur lors de la vérification du token');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password || !confirmPassword) {
      setError('Tous les champs sont requis');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/access/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          newPassword: password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Erreur lors de la réinitialisation');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!tokenValid) {
    return (
      <ThemeProvider theme={theme}>
        <Fade in timeout={700}>
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              px: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: 420,
                p: 4,
                borderRadius: 5,
                boxShadow: '0 8px 40px rgba(0,0,0,.08)',
                textAlign: 'center',
              }}
            >
              <Lock sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} mb={2}>
                Lien invalide
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                  color: '#000',
                  '&:hover': { bgcolor: '#b39d77' },
                }}
              >
                Retour à la connexion
              </Button>
            </Paper>
          </Box>
        </Fade>
      </ThemeProvider>
    );
  }

  if (success) {
    return (
      <ThemeProvider theme={theme}>
        <Fade in timeout={700}>
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              px: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: 420,
                p: 4,
                borderRadius: 5,
                boxShadow: '0 8px 40px rgba(0,0,0,.08)',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <Typography variant="h2" color="white">
                  ✓
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={700} mb={2}>
                Mot de passe réinitialisé !
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion...
              </Typography>
            </Paper>
          </Box>
        </Fade>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Fade in timeout={700}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            px: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: 4,
              borderRadius: 5,
              boxShadow: '0 8px 40px rgba(0,0,0,.08)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Lock sx={{ fontSize: 50, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700} mb={0.5}>
                Nouveau mot de passe
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userEmail}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                variant="standard"
                label="Nouveau mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  sx: fieldSx,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="standard"
                label="Confirmer le mot de passe"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  sx: fieldSx,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disableElevation
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.4,
                  fontWeight: 600,
                  borderRadius: 3,
                  bgcolor: 'primary.main',
                  color: '#000',
                  '&:hover': { bgcolor: '#b39d77' },
                }}
              >
                {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
              </Button>

              <Typography variant="body2" textAlign="center">
                <Button
                  onClick={() => navigate('/login')}
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  Retour à la connexion
                </Button>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </ThemeProvider>
  );
};

export default ResetPassword;

const fieldSx = {
  px: 2,
  py: 1.2,
  borderRadius: 3,
  fontSize: 15,
  bgcolor: '#f5f5f5',
};
